import { Visibility } from '@mui/icons-material'
import { Chip, Theme, Typography, useTheme } from '@mui/material'
import { GridColumns, GridNativeColTypes, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactComponent as LocalityIcon } from 'src/assets/icons/localityIcon.svg'
import { ReactComponent as ServiceStationIcon } from 'src/assets/icons/serviceStationIcon.svg'
import { ReactComponent as StorageIcon } from 'src/assets/icons/storageIcon.svg'
import { GridActionButton } from 'src/components/common/GridActionButton'
import GenericTable from 'src/components/table/GenericTable'
import type { BillingAsset, BillingAssetType } from 'src/generated-types/customer-license'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { billingCostForDateRange, formatBillingCurrency } from '../licenseContainer.action'

const facilityIconInlineStyle = {
  height: 26,
  width: 26,
  verticalAlign: 'middle' as const,
  marginRight: 4,
}

const facilityTypeIcon = (assetType: BillingAssetType, theme: Theme) => {
  if (assetType === 'locality')
    return (
      <LocalityIcon
        color={getThemeColor(theme, MUIThemeColors.primaryMain)}
        style={facilityIconInlineStyle}
        aria-hidden
      />
    )
  if (assetType === 'storage')
    return (
      <StorageIcon
        color={getThemeColor(theme, MUIThemeColors.primaryMain)}
        style={facilityIconInlineStyle}
        aria-hidden
      />
    )
  return (
    <ServiceStationIcon
      color={getThemeColor(theme, MUIThemeColors.primaryMain)}
      style={facilityIconInlineStyle}
      aria-hidden
    />
  )
}

type AssetBillingTableProps = {
  rows: BillingAsset[]
  /** Same range as facility fetch (Filter); cost is monthly rate prorated over these dates. */
  periodFrom: Date | null
  periodTo: Date | null
  onViewLocalityActivityLog?: (asset: BillingAsset) => void
}

export const AssetBillingTable = ({
  rows,
  periodFrom,
  periodTo,
  onViewLocalityActivityLog,
}: AssetBillingTableProps) => {
  const theme = useTheme()
  const { t } = useTranslation('admin-license')

  const typeLabel = (assetType: BillingAssetType) => {
    if (assetType === 'locality') return t('locality')
    if (assetType === 'storage') return t('storage')
    return t('serviceStation')
  }

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'customerName',
        headerName: t('columnCustomer'),
        flex: 1,
        minWidth: 140,
      },
      {
        field: 'assetName',
        headerName: t('columnAssetName'),
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'type',
        headerName: t('columnType'),
        flex: 0.8,
        minWidth: 150,
        sortable: false,
        renderCell: (params: GridRenderCellParams<BillingAsset>) => (
          <Typography component="span" variant="body2" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            {facilityTypeIcon(params.row.type, theme)}
            {typeLabel(params.row.type)}
          </Typography>
        ),
      },
      {
        field: 'status',
        headerName: t('columnStatus'),
        width: 120,
        sortable: false,
        renderCell: (params: GridRenderCellParams<BillingAsset>) => (
          <Chip
            label={params.row.status === 'active' ? t('statusActive') : t('statusInactive')}
            size="small"
            sx={{
              bgcolor:
                params.row.status === 'active'
                  ? getThemeColor(theme, MUIThemeColors.successLight)
                  : getThemeColor(theme, MUIThemeColors.secondaryLight),
              color:
                params.row.status === 'active'
                  ? getThemeColor(theme, MUIThemeColors.white)
                  : getThemeColor(theme, MUIThemeColors.primaryDark),
              fontWeight: 600,
              fontSize: 11,
            }}
          />
        ),
      },
      {
        field: 'periodCost',
        headerName: t('columnCost'),
        width: 130,
        sortable: true,
        valueGetter: (params: { row: BillingAsset }) =>
          billingCostForDateRange(params.row.monthlyRate, periodFrom, periodTo),
        renderCell: (params: GridRenderCellParams<BillingAsset>) => formatBillingCurrency(Number(params.value)),
      },
      {
        field: 'actions',
        type: 'actions' as GridNativeColTypes,
        headerName: '',
        width: 70,
        align: 'right',
        headerAlign: 'right',
        sortable: false,
        renderCell: (params: GridRenderCellParams<BillingAsset>) =>
          params.row.type === 'locality' ? (
            <GridActionButton
              toolTipTitle={t('viewDetails')}
              width="36px"
              height="36px"
              buttonIcon={<Visibility fontSize="small" sx={{ cursor: 'pointer' }} />}
              onClick={() => onViewLocalityActivityLog?.(params.row)}
            />
          ) : null,
      },
    ],
    [t, theme, periodFrom, periodTo, onViewLocalityActivityLog],
  )

  return (
    <GenericTable
      id="license-asset-billing-table"
      columns={columns}
      data={rows}
      enablePagination={false}
      enableToolbar={false}
      enableFilter={false}
      enableColumnOptions={false}
      getRowHeight={() => 40}
      height={`${window.innerHeight - 350}px`}
      autoHeight={false}
      minRowHeight="40px"
      noRowsMessage={t('noFacilities')}
      sx={{
        border: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
        borderRadius: 1,
        '& .MuiDataGrid-columnHeaders': {
          minHeight: '36px !important',
          maxHeight: '36px !important',
        },
        '& .MuiDataGrid-columnHeaderTitle': {
          fontSize: '12px',
          fontWeight: 600,
          color: getThemeColor(theme, MUIThemeColors.secondaryDark),
        },
      }}
    />
  )
}
